/**
 * Pre-flight checks on an uploaded ad config file, in the browser.
 *
 * This is deliberately not a settings parser. It reads only the provenance
 * comments the exporter writes — the header and the per-campaign markers — so
 * it can answer the two questions worth answering before anything is sent
 * anywhere: is this file for this location, and which campaigns does it claim
 * to describe.
 *
 * The full parse, the settings diff and the apply belong on the server, where
 * they can be checked against live platform state. This stays as the client
 * pre-flight even once that exists: catching "wrong location" here saves a
 * round trip and gives an immediate, specific error.
 */

/**
 * Must match AD_CONFIG_FORMAT_VERSION in the backend's adConfig.types.ts.
 *
 * Only *newer* files are rejected: a v1 file still parses here, because the
 * header and campaign markers it validates did not change in v2 — v2 added ad
 * group structure below them.
 */
export const SUPPORTED_FORMAT_VERSION = 2;

export interface AdConfigFileHeader {
  formatVersion: number;
  practiceId: string;
  locationId: string;
  exportedAt: string;
  fingerprint: string;
}

export interface AdConfigFileCampaign {
  platform: string;
  channel: string;
  externalId: string;
  /** The `## ` heading above the marker, which carries the campaign name. */
  heading: string;
}

export type AdConfigFileParse =
  | { ok: true; header: AdConfigFileHeader; campaigns: AdConfigFileCampaign[] }
  | { ok: false; reason: string };

const HEADER_RE =
  /<!--\s*pn:ads-config\s+v(\d+)\s+practice=(\S+)\s+location=(\S+)\s+exported=(\S+)\s+fingerprint=(\S+)\s*-->/;

const CAMPAIGN_RE = /<!--\s*pn:campaign\s+platform=(\S+)\s+channel=(\S+)\s+external=(\S+)\s*-->/g;

const MIN_HEADER_RE = /<!--\s*pn:ads-config\s+v(\d+)/;
const CAMPAIGN_EXT_RE = /<!--\s*pn:campaign\s+[^>]*external=(\S*)\s*-->/g;

export type ImportMode =
  | { mode: 'edit' }
  | { mode: 'create' }
  | { mode: 'invalid'; reason: string };

/**
 * Decide, from the file's markers alone, whether this upload edits existing
 * campaigns or creates new ones.
 *
 * A file is "edit" when at least one campaign carries a platform id
 * (`external=<id>`) — those are live campaigns to reconcile against. A file is
 * "create" when every campaign's `external=` is empty — brand-new campaigns
 * with no platform identity yet. This is what lets an authored file (with only
 * `<!-- pn:ads-config v2 -->` and no practice/location) be recognised without
 * the full export header.
 */
export function detectImportMode(text: string): ImportMode {
  if (!MIN_HEADER_RE.test(text)) {
    return {
      mode: 'invalid',
      reason:
        'This file is missing the PracticeNova header line. It must start with “<!-- pn:ads-config v2 -->”.',
    };
  }
  let any = false;
  let anyWithId = false;
  CAMPAIGN_EXT_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CAMPAIGN_EXT_RE.exec(text)) !== null) {
    any = true;
    if ((match[1] ?? '').trim() !== '') anyWithId = true;
  }
  if (!any) {
    return { mode: 'invalid', reason: 'This file describes no campaigns.' };
  }
  return anyWithId ? { mode: 'edit' } : { mode: 'create' };
}

export function parseAdConfigFile(text: string): AdConfigFileParse {
  const header = HEADER_RE.exec(text);
  if (!header) {
    return {
      ok: false,
      reason:
        'This file has no PracticeNova header, so it was not produced by an ad config export. Export a file first, edit that, and upload it back.',
    };
  }

  const formatVersion = Number(header[1]);
  if (formatVersion > SUPPORTED_FORMAT_VERSION) {
    return {
      ok: false,
      reason: `This file is format v${formatVersion}, but this dashboard understands v${SUPPORTED_FORMAT_VERSION}. It was exported by a newer version of PracticeNova.`,
    };
  }

  const campaigns: AdConfigFileCampaign[] = [];
  // Track the most recent `## ` heading so each marker can be labelled with the
  // campaign name a human will recognise.
  const lines = text.split(/\r?\n/);
  let lastHeading = '';
  for (const line of lines) {
    if (line.startsWith('## ')) {
      lastHeading = line.slice(3).trim();
      continue;
    }
    CAMPAIGN_RE.lastIndex = 0;
    const match = CAMPAIGN_RE.exec(line);
    if (match) {
      campaigns.push({
        platform: match[1],
        channel: match[2],
        externalId: match[3],
        heading: lastHeading,
      });
    }
  }

  return {
    ok: true,
    header: {
      formatVersion,
      practiceId: header[2],
      locationId: header[3],
      exportedAt: header[4],
      fingerprint: header[5],
    },
    campaigns,
  };
}

export type ScopeCheck = { ok: true } | { ok: false; reason: string };

/**
 * Refuse a file exported for a different practice or location.
 *
 * This is the check that stops the most damaging mistake available here —
 * applying one practice's campaign settings to another — and it is cheap,
 * because the exporter stamps both ids into the header.
 */
export function checkFileScope(
  header: AdConfigFileHeader,
  practiceId: string,
  locationId: string,
): ScopeCheck {
  if (header.practiceId !== practiceId) {
    return {
      ok: false,
      reason:
        'This file was exported for a different practice. Open that practice and import it there.',
    };
  }
  if (header.locationId !== locationId) {
    return {
      ok: false,
      reason:
        'This file was exported for a different location of this practice. Switch to that location and import it there.',
    };
  }
  return { ok: true };
}
